import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, Loader2, Phone, MapPin, RefreshCw, FileText, Handshake, CalendarCheck, Briefcase, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { api, inputCls } from './api';

const TYPE_META = {
  cold_call: { icon: Phone, color: 'text-blue-600 bg-blue-50', label: 'Cold call' },
  site_visit: { icon: MapPin, color: 'text-emerald-600 bg-emerald-50', label: 'Site visit' },
  follow_up: { icon: RefreshCw, color: 'text-orange-600 bg-orange-50', label: 'Follow-up' },
  quote_send: { icon: FileText, color: 'text-purple-600 bg-purple-50', label: 'Send quote' },
  quote_close: { icon: Handshake, color: 'text-green-700 bg-green-50', label: 'Close quote' },
  meeting: { icon: CalendarCheck, color: 'text-indigo-600 bg-indigo-50', label: 'Meeting' },
  admin: { icon: Briefcase, color: 'text-slate-600 bg-slate-100', label: 'Admin' },
};
const TARGET_LABELS = { cold_calls: 'Cold calls', site_visits: 'Site visits', follow_ups: 'Follow-ups', quotes_to_send: 'Quotes to send', quotes_to_close: 'Quotes to close' };

const PlanView = ({ doc }) => {
  const p = doc.plan || {};
  return (
    <div className="space-y-4" data-testid="ai-plan-view">
      <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-white border border-violet-100">
        <p className="text-xs text-violet-700 font-medium mb-1">Plan for {doc.person_name} • {new Date(doc.created_at).toLocaleString('en-IN')}</p>
        <p className="text-sm text-slate-800" data-testid="ai-plan-summary">{p.summary}</p>
      </div>
      {p.weekly_targets && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="ai-plan-weekly-targets">
          {Object.entries(TARGET_LABELS).map(([k, l]) => (
            <div key={k} className="p-3 rounded-lg bg-white border border-slate-200 text-center">
              <p className="text-2xl font-bold text-slate-900">{p.weekly_targets[k] ?? 0}</p>
              <p className="text-xs text-slate-500">{l}</p>
            </div>
          ))}
        </div>
      )}
      {p.focus_areas?.length > 0 && (
        <ul className="space-y-1 text-sm text-slate-700" data-testid="ai-plan-focus">
          {p.focus_areas.map((f, i) => <li key={i} className="flex gap-2"><span className="text-violet-500 font-bold">{i + 1}.</span>{f}</li>)}
        </ul>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {(p.days || []).map(d => (
          <div key={d.day} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`ai-plan-day-${d.day}`}>
            <p className="text-sm font-semibold text-slate-900 mb-2">{d.day}</p>
            <div className="space-y-2">
              {(d.activities || []).map((a, i) => {
                const m = TYPE_META[a.type] || TYPE_META.admin;
                return (
                  <div key={i} className="flex gap-2">
                    <span className={`p-1.5 h-fit rounded-md ${m.color}`}><m.icon className="w-3.5 h-3.5" /></span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-800">{a.customer ? `${a.customer} — ` : ''}<span className="font-normal">{a.action}</span></p>
                      {a.why && <p className="text-[11px] text-slate-400">{a.why}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {p.risks?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="ai-plan-risks">
          <p className="flex items-center gap-2 font-semibold mb-1"><ShieldAlert className="w-4 h-4" /> Risks</p>
          <ul className="list-disc pl-5 space-y-0.5">{p.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
    </div>
  );
};

export const AIPlanner = ({ fy, people = [], defaultPersonId = '' }) => {
  const [personId, setPersonId] = useState(defaultPersonId);
  const [notes, setNotes] = useState('');
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!personId && people.length) return;
    try {
      const res = await api(`/ai-plans${personId ? `?person_id=${personId}` : ''}`);
      setPlans(res.plans); setSelected(res.plans[0] || null);
    } catch { setPlans([]); setSelected(null); }
  }, [personId, people.length]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const doc = await api('/ai-plan', { method: 'POST', body: JSON.stringify({ person_id: personId || null, fiscal_year: fy, notes: notes || null }) });
      setPlans([doc, ...plans]); setSelected(doc);
      toast.success('Weekly plan ready');
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-violet-200 p-5 space-y-4" data-testid="ai-planner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> AI Weekly Action Plan</h3>
          <p className="text-xs text-slate-500">Built from your target gap, quarter pace, open quotes, unquoted enquiries, follow-ups and prospects</p>
        </div>
        {plans.length > 1 && (
          <select value={selected?.id || ''} onChange={e => setSelected(plans.find(p => p.id === e.target.value))} className="px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ai-plan-history">
            {plans.map(p => <option key={p.id} value={p.id}>{new Date(p.created_at).toLocaleString('en-IN')}</option>)}
          </select>
        )}
      </div>
      <div className="flex flex-col md:flex-row gap-3">
        {people.length > 0 && (
          <select value={personId} onChange={e => setPersonId(e.target.value)} className={`${inputCls} md:w-56`} data-testid="ai-plan-person-select">
            <option value="">Select salesperson</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} className={`${inputCls} flex-1`} data-testid="ai-plan-notes"
          placeholder="Your inputs & commitments this week (e.g. 'Visiting Hosur on Wed, expecting PO from JMI, on leave Friday')" />
        <button onClick={generate} disabled={busy || (people.length > 0 && !personId)} data-testid="ai-plan-generate-btn"
          className="flex items-center justify-center gap-2 px-5 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 whitespace-nowrap">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? 'Planning… (~40s)' : 'Generate plan'}
        </button>
      </div>
      {selected ? <PlanView doc={selected} /> : <p className="text-sm text-slate-400 text-center py-6" data-testid="ai-plan-empty">No plan yet — add your inputs and generate one.</p>}
    </div>
  );
};
